import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Divider,
  Grid
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import JoditEditor from 'jodit-react';
import { Buffer } from 'buffer';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { creators as SummaryActions } from '../../../Reducers/Reporting/Summary';
import { creators as ReportSettingActions } from '../../../Reducers/Reporting/ReportSetting';
import { makeDimDate, financialMonth, printFormat } from '../../../Utils/Functions';
import YearSelector from '../../../Common/Selectors/YearSelectorSingle';
import MonthSelector from '../../../Common/Selectors/MonthSelector';
import appdata from '../../../Services/appdata';
import ApiSummary from '../../../Api/ApiSummary';
import './style.css';

function ExecutiveSummary({  
  defaultYear,
  defaultMonth,  
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  exporting,
  printFlag,
  reportPageStructure,
  defaultHandler,
  handleExportPDF
}) {
  const dispatch = useDispatch();

  // ---- Safe Redux selectors (always return defined values) ----
  const summary = useSelector((s) => s?.Reporting?.Summary?.summary ?? '');
  const selectedYears = useSelector((s) => s?.Reporting?.Summary?.selectedYears ?? [defaultYear]);
  const label = useSelector((s) => s?.Reporting?.Summary?.label ?? String(defaultYear));
  const selectedMonths = useSelector((s) => s?.Reporting?.Summary?.selectedMonths ?? [defaultMonth]);
  const month_label = useSelector((s) => s?.Reporting?.Summary?.month_label ?? String(defaultMonth));
  const lastFetchedCompany = useSelector((s) => s?.Reporting?.Default?.defaultCompany ?? null);
  const lastFetchedDimDate = useSelector((s) => s?.Reporting?.Default?.defaultDimDate ?? null);

  const reportingData = {
    balanceSheet: useSelector((s) => s?.Reporting?.BalanceSheet ?? {}),
    cashFlow: useSelector((s) => s?.Reporting?.CashFlow ?? {}),
    debtors: useSelector((s) => s?.Reporting?.debtors ?? { summaryData: [], detailData: [] }),
    expenses: useSelector((s) => s?.Reporting?.Expenses ?? { summaryData: [], detailData: [] }),
    fees: useSelector((s) => s?.Reporting?.fees ?? { summaryData: [], detailData: [] }),
    feesProjection: useSelector((s) => s?.Reporting?.feesprojection ?? { summaryData: [], detailData: [] }),
    financialYTD: useSelector((s) => s?.Reporting?.financialYTD ?? { summaryData: [], detailData: [] }),
    kpi: useSelector((s) => s?.Reporting?.KPI ?? { summaryData: [], target: [] }),
    opportunity: useSelector((s) => s?.Reporting?.Opportunity ?? { summaryData: [], detailData: [] }),
    people: useSelector((s) => s?.Reporting?.People ?? { summaryData: [], detailData: { Left: [], Right: [] } }),
    projectedCashFlow: useSelector((s) => s?.Reporting?.ProjectedCashFlow ?? { topData: [], middleData: [], bottomData: [] }),
    projectedFinancialPerformance: useSelector((s) => s?.Reporting?.projectedfinancialPerformance ?? { summaryData: [], detailData: [] }),
    projectPerformance: useSelector((s) => s?.Reporting?.ProjectPerformance ?? { summaryData: [], detailData: [] }),
    workGenerated: useSelector((s) => s?.Reporting?.WorkGenerated ?? { queryData: [] }),
    workInHand: useSelector((s) => s?.Reporting?.WorkInHand ?? { summaryData: [], detailData: [] }),
    yearlyFinancialYTD: useSelector((s) => s?.Reporting?.yearlyfinancialYTD ?? { summaryData: [], detailData: [] })
  };

  // ---- Local UI state ----
  const editor = useRef(null);
  const [editable, setEditable] = useState(false);
  const [originalContext, setOriginalContext] = useState('');
  const [clipContext, setClipContext] = useState('');
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);

  // ---- Helpers ----
  const decodeBase64ToUtf8 = useCallback((b64MaybeWithSpaces) => {
    try {
      if (!b64MaybeWithSpaces) return '';
      // historical: server sometimes stores with spaces; normalize to '+'
      const normalized = b64MaybeWithSpaces.replace(/ /g, '+');
      return Buffer.from(normalized, 'base64').toString();
    } catch {
      return '';
    }
  }, []);

  const encodeUtf8ToBase64 = useCallback((text) => {
    try {
      return Buffer.from(text ?? '', 'utf8').toString('base64');
    } catch {
      return '';
    }
  }, []);

  const stripHtmlTags = useCallback((html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }, []);

  // ---- Page mapping (stable & memoized) ----
  const pageKeyMapping = useMemo(() => {
    const pages = Array.isArray(reportPageStructure) ? reportPageStructure : [];
    const mapping = {};
    pages.forEach((page) => {
      const titleKey = String(page?.title ?? '').toLowerCase().replace(/\s+/g, '');
      switch (titleKey) {
        case 'balancesheet':
          mapping[page.id] = 'balanceSheet';
          break;
        case 'cashflow':
          mapping[page.id] = 'cashFlow';
          break;
        case 'debtors':
          mapping[page.id] = 'debtors';
          break;
        case 'expenses':
          mapping[page.id] = 'expenses';
          break;
        case 'fees':
          mapping[page.id] = 'fees';
          break;
        case 'feeprojection':
          mapping[page.id] = 'feesProjection';
          break;
        case 'financialperformanceytd':
        case 'yearlyfinancialperformance': // alias retained from original mapping
        case 'yearlyfinancialytd':
          mapping[page.id] = 'yearlyFinancialYTD';
          break;
        case 'projectperformance':
          mapping[page.id] = 'projectPerformance';
          break;
        case 'workinhand':
          mapping[page.id] = 'workInHand';
          break;
        case 'opportunities':
          mapping[page.id] = 'opportunity';
          break;
        case 'workgenerated':
          mapping[page.id] = 'workGenerated';
          break;
        case 'projectedfinancialperformance':
          mapping[page.id] = 'projectedFinancialPerformance';
          break;
        case 'projectedcashflow':
          mapping[page.id] = 'projectedCashFlow';
          break;
        case 'people':
          mapping[page.id] = 'people';
          break;
        case 'businessperformance':
          mapping[page.id] = 'kpi';
          break;
        default:
          break;
      }
    });
    return mapping;
  }, [reportPageStructure]);

  const initialSelectedPages = useMemo(() => {
    const initial = {};
    const pages = Array.isArray(reportPageStructure) ? reportPageStructure : [];
    pages.forEach((p) => {
      const key = pageKeyMapping[p.id];
      if (key) initial[key] = true;
    });
    return initial;
  }, [reportPageStructure, pageKeyMapping]);

  const [selectedPages, setSelectedPages] = useState(initialSelectedPages);

  // Keep selected pages in sync with incoming structure
  useEffect(() => {
    setSelectedPages(initialSelectedPages);
  }, [initialSelectedPages]);

  // ---- Window width listener ----
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    dispatch(
      SummaryActions.summaryUpdateFilter({
        label: String(defaultYear),
        month_label: String(defaultMonth),
        selectedYears: [defaultYear],
        selectedMonths: [defaultMonth],
        dimDate: defaultDimDate,
      }),
    );
  }, [dispatch, defaultYear, defaultMonth, defaultDimDate]);

  const triggerExecutiveSummaryRequest = useCallback(() => {
    dispatch(SummaryActions.summaryGetRequest(defaultDimDate));
  }, [dispatch, defaultDimDate]);

  useEffect(() => {
    if (!lastFetchedCompany || !lastFetchedDimDate) {
      triggerExecutiveSummaryRequest();
    }
  }, [defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerExecutiveSummaryRequest]);

  useEffect(() => {
    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    if (
      valuesChanged &&
      lastFetchedCompany &&
      lastFetchedDimDate
    ) {
      triggerExecutiveSummaryRequest();
    }
  }, [selectedCompany, defaultDimDate, lastFetchedCompany, lastFetchedDimDate, triggerExecutiveSummaryRequest]);

  // ---- Reflect summary into editor (decoded) ----
  useEffect(() => {
    if (summary) {
      const content = decodeBase64ToUtf8(summary);
      setClipContext(content);
      setOriginalContext(content);
    } else {
      setClipContext('');
      setOriginalContext('');
    }
  }, [summary, decodeBase64ToUtf8]);

  // ---- Toolbar config ----
  const buttons = [
    'undo', 'redo', '|',
    'bold', 'strikethrough', 'underline', 'italic', '|',
    'superscript', 'subscript', '|',
    'align', '|',
    'ul', 'ol', 'outdent', 'indent', '|',
    'font', 'fontsize', 'brush', 'paragraph', '|',
    'image', 'link', 'table', '|',
    'hr', 'eraser', 'copyformat', '|',
    'fullsize', 'selectall', 'print', '|',
    'source'
  ];

  const editorConfig = {
    readonly: false,
    toolbar: true,
    spellcheck: true,
    language: 'en',
    toolbarButtonSize: 'middle',
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    defaultActionOnPaste: 'insert_clear_html',
    buttons,
    uploader: {
      insertImageAsBase64URI: true
    }
  };

  // ---- Handlers ----
  const handlePageToggle = (pageKey) => {
    setSelectedPages((prev) => ({ ...prev, [pageKey]: !prev[pageKey] }));
  };

  const handleSelectAllPages = () => {
    const keys = Object.keys(selectedPages);
    if (keys.length === 0) return;
    const allSelected = keys.every((k) => !!selectedPages[k]);
    const next = {};
    keys.forEach((k) => (next[k] = !allSelected));
    setSelectedPages(next);
  };

  const onEdit = () => setEditable(true);

  const onClose = () => {
    setClipContext(originalContext);
    setEditable(false);
  };

  const onSave = () => {
    const enc = encodeUtf8ToBase64(clipContext);
    const _DimDate = makeDimDate(selectedYears[0], selectedMonths[0], defaultStartMonth);
    dispatch(SummaryActions.summarySetRequest(_DimDate, enc));
    setOriginalContext(clipContext);
    setEditable(false);
  };

  const handleYear = async (event) => {
    appdata.localClear();
    const nextYear = event?.selectedYears?.[0] ?? defaultYear;
    const month0 = selectedMonths?.[0] ?? defaultMonth;

    const _dimDate = makeDimDate(nextYear, month0, defaultStartMonth);

    // Update ReportSetting Year/Month with financialMonth correction
    if (month0 < defaultStartMonth) {
      await dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          Year: nextYear - 1,
          Month: financialMonth(month0, defaultStartMonth)
        })
      );
    } else {
      await dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          Year: nextYear,
          Month: financialMonth(month0, defaultStartMonth)
        })
      );
    }

    dispatch(SummaryActions.summaryGetRequest(_dimDate));
    dispatch(SummaryActions.summaryUpdateFilter(event));
    defaultHandler({
      defaultDimDate: _dimDate,
      defaultYear: nextYear,
      defaultMonth: month0
    });
  };

  const handleMonth = async (event) => {
    appdata.localClear();

    const nextMonth0 = event?.selectedMonths?.[0] ?? defaultMonth;
    const year0 = selectedYears?.[0] ?? defaultYear;

    const _dimDate =
      nextMonth0 < defaultStartMonth
        ? printFormat.dateYYYYMMDD(year0 - 1, financialMonth(nextMonth0, defaultStartMonth))
        : printFormat.dateYYYYMMDD(year0, financialMonth(nextMonth0, defaultStartMonth));

    if (nextMonth0 < defaultStartMonth) {
      await dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          Year: year0 - 1,
          Month: financialMonth(nextMonth0, defaultStartMonth)
        })
      );
    } else {
      await dispatch(
        ReportSettingActions.reportSettingUpdateRequest({
          Year: year0,
          Month: financialMonth(nextMonth0, defaultStartMonth)
        })
      );
    }

    dispatch(SummaryActions.summaryGetRequest(_dimDate));
    dispatch(SummaryActions.summaryUpdateFilter(event));
    defaultHandler({
      defaultDimDate: _dimDate,
      defaultYear: year0,
      defaultMonth: nextMonth0
    });
  };

  const openAIDialog = () => setAiDialogOpen(true);
  const closeAIDialog = () => setAiDialogOpen(false);

  // ---- AI Summary generation (robust to missing slices) ----
  const generateAISummary = async () => {
    setAiDialogOpen(false);
    setAiGenerating(true);

    try {
      let priorSummaries = [];
      const reporting_data = {};

      // Balance Sheet
      if (selectedPages.balanceSheet) {
        const q = reportingData?.balanceSheet?.queryResult ?? {};
        reporting_data.balanceSheet = {
          Assets: q?.Assets ?? [],
          Liability: q?.Liability ?? [],
          NetWorth: q?.NetWorth ?? [],
          CurrentRatio: q?.CurrentRatio ?? 0,
          KpiTarget: q?.KpiTarget ?? 0
        };
      }

      // Cash Flow (bottomData grouped by Date)
      if (selectedPages.cashFlow) {
        const topData = reportingData?.cashFlow?.topData ?? [];
        const middleData = reportingData?.cashFlow?.middleData ?? [];
        const bottom = reportingData?.cashFlow?.bottomData ?? [];

        const groupedByDate = bottom.reduce((acc, item) => {
          const date = item?.Date ?? '';
          if (!acc[date]) {
            acc[date] = {
              Date: date,
              FY: item?.FY ?? '',
              SafetyBankBalance: item?.SafetyBankBalance ?? 0,
              CashBalance: 0
            };
          }
          acc[date].CashBalance += Number(item?.CashBalance ?? 0);
          return acc;
        }, {});

        reporting_data.cashFlow = {
          topData,
          middleData,
          bottomData: Object.values(groupedByDate)
        };
      }

      // Debtors
      if (selectedPages.debtors) {
        reporting_data.debtors = {
          summaryData: reportingData?.debtors?.summaryData ?? [],
          detailData: reportingData?.debtors?.detailData ?? []
        };
      }

      // Expenses (grouped)
      if (selectedPages.expenses) {
        const detail = reportingData?.expenses?.detailData ?? [];
        const grouped = detail.reduce((acc, item) => {
          const key = [
            item?.FY ?? '',
            item?.AccountType ?? '',
            item?.AccountSubType ?? '',
            item?.AccountNumber ?? '',
            item?.AccountName ?? ''
          ].join('_');

          if (!acc[key]) {
            acc[key] = {
              FY: item?.FY ?? '',
              AccountType: item?.AccountType ?? '',
              AccountSubType: item?.AccountSubType ?? '',
              AccountNumber: item?.AccountNumber ?? '',
              AccountName: item?.AccountName ?? '',
              ActualExpenses: 0,
              ForecastExpenses: 0
            };
          }
          acc[key].ActualExpenses += Number(item?.ActualExpenses ?? 0);
          acc[key].ForecastExpenses += Number(item?.ForecastExpenses ?? 0);
          return acc;
        }, {});

        reporting_data.expenses = {
          summaryData: reportingData?.expenses?.summaryData ?? [],
          detailData: Object.values(grouped)
        };
      }

      // Fees
      if (selectedPages.fees) {
        reporting_data.fees = {
          summaryData: reportingData?.fees?.summaryData ?? [],
          detailData: reportingData?.fees?.detailData ?? []
        };
      }

      // Fee Projection (grouped)
      if (selectedPages.feesProjection) {
        const detail = reportingData?.feesProjection?.detailData ?? [];
        const grouped = detail.reduce((acc, item) => {
          const key = [
            item?.FY ?? '',
            item?.ProjectType ?? '',
            item?.ProjectSubType ?? '',
            item?.ProjectId ?? '',
            item?.ProjectName ?? ''
          ].join('_');

          if (!acc[key]) {
            acc[key] = {
              ProjectType: item?.ProjectType ?? '',
              ProjectSubType: item?.ProjectSubType ?? '',
              ProjectName: item?.ProjectName ?? '',
              ProjectId: item?.ProjectId ?? '',
              FY: item?.FY ?? '',
              ActualFees: 0,
              ForecastFees: 0
            };
          }
          acc[key].ActualFees += Number(item?.ActualFees ?? 0);
          acc[key].ForecastFees += Number(item?.ForecastFees ?? 0);
          return acc;
        }, {});

        reporting_data.feesProjection = {
          summaryData: reportingData?.feesProjection?.summaryData ?? [],
          detailData: Object.values(grouped)
        };
      }

      // Financial YTD (current) & Yearly
      if (selectedPages.financialYTD) {
        reporting_data.financialYTD = {
          summaryData: reportingData?.financialYTD?.summaryData ?? [],
          detailData: reportingData?.financialYTD?.detailData ?? []
        };
      }
      if (selectedPages.yearlyFinancialYTD) {
        reporting_data.yearlyFinancialYTD = {
          summaryData: reportingData?.yearlyFinancialYTD?.summaryData ?? [],
          detailData: reportingData?.yearlyFinancialYTD?.detailData ?? []
        };
      }

      // KPI (collapsed)
      if (selectedPages.kpi) {
        const summary = reportingData?.kpi?.summaryData ?? [];
        const grouped = summary.reduce((acc, item) => {
          const key = `${item?.Group ?? ''}_${item?.Name ?? ''}`;
          if (!acc[key]) {
            acc[key] = {
              Category: item?.Category ?? '',
              DesiredOutcome: item?.DesiredOutcome ?? '',
              DisplayFormat: item?.DisplayFormat ?? '',
              Enable: item?.Enable ?? false,
              Group: item?.Group ?? '',
              Lower: item?.Lower ?? 0,
              Name: item?.Name ?? '',
              Result: 0,
              Target: 0
            };
          }
          acc[key].Target += Number(item?.Target ?? 0);
          acc[key].Result += Number(item?.Result ?? 0);
          return acc;
        }, {});
        reporting_data.kpi = {
          summaryData: Object.values(grouped),
          target: reportingData?.kpi?.target ?? []
        };
      }

      // Opportunity
      if (selectedPages.opportunity) {
        reporting_data.opportunity = {
          summaryData: reportingData?.opportunity?.summaryData ?? [],
          detailData: reportingData?.opportunity?.detailData ?? []
        };
      }

      // People (merge Right with matching Left metrics if present)
      if (selectedPages.people) {
        const people = reportingData?.people ?? {};
        const left = people?.detailData?.Left ?? [];
        const right = people?.detailData?.Right ?? [];

        const mergedRight = right.map((r) => {
          const match = left.find(
            (l) =>
              l?.EmployeeRole === r?.EmployeeRole &&
              l?.Group === r?.Group &&
              l?.Date === r?.Date
          );
          return match
            ? {
                ...r,
                ActualFteEquivalentMTD: match?.ActualFteEquivalentMTD ?? r?.ActualFteEquivalentMTD,
                FteEquivalent: match?.FteEquivalent ?? r?.FteEquivalent
              }
            : r;
        });

        reporting_data.people = {
          summaryData: people?.summaryData ?? [],
          detailData: mergedRight
        };
      }

      // Projected Cash Flow
      if (selectedPages.projectedCashFlow) {
        reporting_data.projectedCashFlow = {
          topData: reportingData?.projectedCashFlow?.topData ?? [],
          middleData: reportingData?.projectedCashFlow?.middleData ?? [],
          bottomData: reportingData?.projectedCashFlow?.bottomData ?? []
        };
      }

      // Projected Financial Performance
      if (selectedPages.projectedFinancialPerformance) {
        reporting_data.projectedFinancialPerformance = {
          summaryData: reportingData?.projectedFinancialPerformance?.summaryData ?? [],
          detailData: reportingData?.projectedFinancialPerformance?.detailData ?? []
        };
      }

      // Project Performance
      if (selectedPages.projectPerformance) {
        reporting_data.projectPerformance = {
          summaryData: reportingData?.projectPerformance?.summaryData ?? [],
          detailData: reportingData?.projectPerformance?.detailData ?? []
        };
      }

      // Work Generated
      if (selectedPages.workGenerated) {
        reporting_data.workGenerated = reportingData?.workGenerated?.queryData ?? [];
      }

      // Work In Hand
      if (selectedPages.workInHand) {
        reporting_data.workInHand = {
          summaryData: reportingData?.workInHand?.summaryData ?? [],
          detailData: reportingData?.workInHand?.detailData ?? []
        };
      }

      // ---- Pull prior summaries and generate ----
      const res = await ApiSummary.getAllSummaries(defaultDimDate);
      if (res?.status === 200) {
        priorSummaries = Array.isArray(res?.data) ? res.data : [];
      } else if (res?.message) {
        throw new Error(res.message);
      }

      const parsedPriorSummaries = [];
      let htmlStructureFlag = false;

      for (const s of priorSummaries) {
        const raw = s?.summary ?? '';
        const content = decodeBase64ToUtf8(raw);
        let plainText = '';
        if (htmlStructureFlag === false && content.includes('Profitability')) {
          htmlStructureFlag = true;
          plainText = content;
        } else {
          plainText = stripHtmlTags(content);
        }
        parsedPriorSummaries.push({
          dimDateKey: s?.dimDateKey ?? '',
          summary: plainText
        });
      }

      const result = await ApiSummary.generateAISummary(
        parsedPriorSummaries,
        reporting_data,
        defaultDimDate
      );

      if (result?.summary) {
        setClipContext(result.summary);
      } else if (result?.error) {
        throw new Error(result.error);
      } else {
        throw new Error('No summary generated');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating AI summary:', error);
      alert('Failed to generate AI summary. Please try again. ' + (error?.message || ''));
    } finally {
      setAiGenerating(false);
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 108px)', background: 'white' }}>
      <div className="wrapper">
        <div id="executive_summary_selector" style={{ display: 'flex', paddingLeft: 8 }}>
          <YearSelector
            selectedYears={selectedYears?.length ? selectedYears : [new Date().getFullYear()]}
            label={label}
            onChange={handleYear}
          />

          <MonthSelector
            selectedMonths={selectedMonths}
            month_label={month_label}
            defaultStartMonth={defaultStartMonth}
            onChange={handleMonth}
          />

          <IconButton
            sx={{ ml: 1, cursor: 'pointer', opacity: 0.6, display: 'flex', alignItems: 'center' }}
            onClick={() => (editable ? onSave() : onEdit())}
          >
            {editable ? <SaveOutlinedIcon /> : <EditOutlinedIcon />}
          </IconButton>

          {editable && (
            <>
              <IconButton
                sx={{ ml: 1, cursor: 'pointer', opacity: aiGenerating ? 0.3 : 0.6 }}
                onClick={openAIDialog}
                disabled={aiGenerating}
                title="Generate AI Executive Summary"
              >
                {aiGenerating ? <CircularProgress size={20} /> : <SmartToyOutlinedIcon />}
              </IconButton>

              <IconButton sx={{ ml: 1, cursor: 'pointer', opacity: 0.6 }} onClick={onClose}>
                <CloseOutlinedIcon />
              </IconButton>
            </>
          )}

          {printFlag && (
            <Button
              onClick={handleExportPDF}
              disabled={exporting}
              sx={{
                display: exporting ? 'none' : 'block',
                float: 'right',
                position: 'absolute !important',
                right: 20,
                fontFamily: 'Calibri !important',
                p: 0,
                mt: 1,
                fontSize: 16
              }}
            >
              {width < 1310 && width > 1230 ? 'Export to PDF' : 'Print View'}
            </Button>
          )}
        </div>
      </div>

      <div id="excutive-summary" style={{ height: 'calc(100vh - 145px)', overflow: 'auto' }}>
        {editable ? (
          <JoditEditor
            tabIndex={1}
            ref={editor}
            value={clipContext}
            config={editorConfig}
            onChange={() => {}}
            onBlur={(newContent) => setClipContext(newContent)}
          />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: clipContext }} />
        )}
      </div>

      {/* AI Summary Dialog */}
      <Dialog open={aiDialogOpen} onClose={closeAIDialog} maxWidth="md" fullWidth>
        <DialogTitle>Generate AI Executive Summary</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Select Data Sources
            </Typography>

            <Box
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Choose which reporting pages to include in the analysis
              </Typography>
              <Button size="small" onClick={handleSelectAllPages} sx={{ textTransform: 'none' }}>
                {Object.values(selectedPages).every((v) => v) ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <Grid container spacing={2}>
              {Array.isArray(reportPageStructure) && reportPageStructure.length > 0 ? (
                reportPageStructure.map((page) => {
                  const pageKey = pageKeyMapping[page.id];
                  if (!pageKey) return null;
                  return (
                    <Grid key={page.id} display="grid" size={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!selectedPages[pageKey]}
                            onChange={() => handlePageToggle(pageKey)}
                            size="small"
                          />
                        }
                        label={page.title}
                      />
                    </Grid>
                  );
                })
              ) : (
                <Grid display="grid" size={12}>
                  <Typography variant="body2" color="text.secondary">
                    No reporting pages available
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> The AI will analyze the last 12 months of executive summaries
              and data from the selected reporting pages for the current period.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAIDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={generateAISummary}
            color="primary"
            variant="contained"
            disabled={Object.values(selectedPages).every((val) => !val)}
          >
            Generate Summary
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

ExecutiveSummary.propTypes = {  
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,  
  defaultStartMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  exporting: PropTypes.bool.isRequired,
  printFlag: PropTypes.bool.isRequired,
  reportPageStructure: PropTypes.array.isRequired,
  defaultHandler: PropTypes.func.isRequired,
  handleExportPDF: PropTypes.func.isRequired,
};

export default ExecutiveSummary;
